import axiosConfig from "./axiosConfig";

// Mirrors the BE's `lead_bulk_create.SiteSkipped` shape.
export interface BulkCreateSkipped {
  leadId: string;
  reason: string;
}

// Mirrors `lead_bulk_create.OrgPlan` minus the in-batch dedupe key
// (the BE strips it from the API surface).
export interface BulkCreateOrgPlan {
  // List of roles the org plays across the leads in this batch.
  // Typically a single role, but the same brand can appear as both
  // landlord and tenant (e.g. owner-occupier-style leads where both
  // decisions resolve to the same name) and we want to render that
  // as "(landlord+tenant, 1 lead)" rather than picking one.
  roles: ("tenant" | "landlord")[];
  name: string;
  leadIds: string[];
  // Set on `orgsToReuse` entries; absent on `orgsToCreate`.
  orgId?: string | null;
}

export interface BulkCreatePreview {
  sitesToCreate: number;
  skipped: BulkCreateSkipped[];
  orgsToReuse: BulkCreateOrgPlan[];
  orgsToCreate: BulkCreateOrgPlan[];
}

export interface BulkCreatePreviewResponse {
  preview: BulkCreatePreview;
}

export interface BulkCreatedSite {
  siteId: string;
  leadId: string;
  name: string;
  tenantOrgId: string | null;
  landlordOrgId: string | null;
}

export interface BulkCreatedOrg {
  orgId: string;
  name: string;
  role: "tenant" | "landlord";
}

// Result shape the long-running operation writes back into DDB on
// completion. Not the immediate response from POST - the FE polls
// `/operations/<key>` and reads `result` once `status === "completed"`.
export interface BulkCreateOperationResult {
  createdSites: BulkCreatedSite[];
  createdOrgs: BulkCreatedOrg[];
  skipped: BulkCreateSkipped[];
}

// Immediate response from POST /leads/bulk-create-sites: the
// operation key + siteId the FE needs to poll for completion. The
// actual create work happens in the long-running-operation Lambda;
// the FE polls /operations/{key}?siteId=... until status is
// completed/failed.
export interface BulkCreatePendingResponse {
  status: "pending";
  operationKey: string;
  siteId: string;
  leadCount: number;
}

export const postBulkCreateSitesPreview = async (
  authToken: string,
  leadIds: string[]
): Promise<BulkCreatePreviewResponse> => {
  const { data } = await axiosConfig.post(
    "/leads/bulk-create-sites/preview",
    { leadIds },
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

export const postBulkCreateSites = async (
  authToken: string,
  leadIds: string[]
): Promise<BulkCreatePendingResponse> => {
  const { data } = await axiosConfig.post(
    "/leads/bulk-create-sites",
    { leadIds },
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};
